import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  CheckCircle as SavedIcon,
  OpenInNew as ViewIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { ChordDiagram } from "../Components/ChordViewer";
import { CustomChordVariation, Song } from "../Entities/SongModel";
import { apiCreateSong, apiFetchSong, apiUpdateSong } from "../utils/useDb";
import { useUser } from "../utils/useUser";
import { bodyToText, parseTextToBody } from "../utils/songEditorUtils";
import { SongBody } from "./Song/components/SongBody";

const STRINGS_LABELS = ["6 (E)", "5 (A)", "4 (D)", "3 (G)", "2 (B)", "1 (e)"];

export default function EditSongPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();

  const searchParams = new URLSearchParams(location.search);
  const initialIdParam = searchParams.get("id");
  const cloneIdParam = searchParams.get("cloneId");

  // Song ID state
  const [songId, setSongId] = useState<number | null>(
    initialIdParam ? parseInt(initialIdParam, 10) : null
  );
  const [isClone, setIsClone] = useState<boolean>(Boolean(cloneIdParam));

  // Form Fields
  const [title, setTitle] = useState<string>("");
  const [artist, setArtist] = useState<string>("");
  const [difficulty, setDifficulty] = useState<string>("intermediate");
  const [key, setKey] = useState<string>("");
  const [tempo, setTempo] = useState<string>("");
  const [capo, setCapo] = useState<string>("");
  const [rawContent, setRawContent] = useState<string>("");
  const [customChords, setCustomChords] = useState<Record<string, CustomChordVariation>>({});

  // Loading & Save Status
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(initialIdParam || cloneIdParam));
  const [saveStatus, setSaveStatus] = useState<"idle" | "saved" | "saving" | "unsaved" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Tabs for mobile / split view toggle
  const [activeTab, setActiveTab] = useState<number>(0);

  // Custom Chord Editor input state
  const [customChordName, setCustomChordName] = useState<string>("");
  const [customFrets, setCustomFrets] = useState<string[]>(["x", "0", "2", "2", "2", "0"]); // A major default
  const [customCapo, setCustomCapo] = useState<number>(0);

  // Tracking dirty state for auto-save
  const isDirtyRef = useRef<boolean>(false);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isSavingRef = useRef<boolean>(false);

  // Load existing song or cloned song
  useEffect(() => {
    let isMounted = true;

    async function loadData() {
      const targetId = songId || (cloneIdParam ? parseInt(cloneIdParam, 10) : null);
      if (!targetId) {
        setIsLoading(false);
        return;
      }

      try {
        const loaded = await apiFetchSong(targetId);
        if (loaded && isMounted) {
          setTitle(cloneIdParam ? `${loaded.title || ""} (My Version)`.trim() : loaded.title || "");
          setArtist(loaded.artist || "");
          setDifficulty(loaded.difficulty || "intermediate");
          setKey(loaded.key || "");
          setTempo(loaded.tempo ? loaded.tempo.toString() : "");
          setCapo(loaded.capo || "");
          setCustomChords(loaded.custom_chords || {});
          setRawContent(bodyToText(loaded.body || []));
          if (!cloneIdParam) {
            setSaveStatus("saved");
          }
        }
      } catch (err: any) {
        if (isMounted) setErrorMessage(err.message || "Failed to load song");
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    loadData();
    return () => {
      isMounted = false;
    };
  }, [songId, cloneIdParam]);

  // Determine Hebrew layout
  const isHebrew = useMemo(() => {
    return /[\u0590-\u05FF]/.test(title + rawContent);
  }, [title, rawContent]);

  // Live parsed body for preview
  const parsedBody = useMemo(() => {
    return parseTextToBody(rawContent, customChords);
  }, [rawContent, customChords]);

  // Save handler (used by both manual click and debounced auto-save)
  const performSave = useCallback(async () => {
    if (isSavingRef.current) return;
    if (!title.trim() && !rawContent.trim()) return;

    isSavingRef.current = true;
    setSaveStatus("saving");
    setErrorMessage(null);

    const payload: Partial<Song> = {
      title: title.trim() || "Untitled Song",
      artist: artist.trim() || (user?.name ? user.name : "Custom"),
      difficulty: (difficulty as any) || undefined,
      key: key.trim() || undefined,
      tempo: tempo.trim() ? parseInt(tempo.trim(), 10) : undefined,
      capo: capo.trim() || undefined,
      custom_chords: customChords,
      body: parsedBody,
      owner: user?.id || user?.email || "system",
    };

    try {
      if (songId && !isClone) {
        // Update existing song
        await apiUpdateSong(songId, payload);
        setSaveStatus("saved");
        isDirtyRef.current = false;
      } else {
        // Create new song (or new clone copy)
        const created = await apiCreateSong(payload);
        setSongId(created.id);
        setIsClone(false);
        setSaveStatus("saved");
        isDirtyRef.current = false;
        // Update URL to match saved song id without full page reload
        window.history.replaceState(null, "", `/EditSong?id=${created.id}`);
      }
    } catch (err: any) {
      console.error("Save error:", err);
      setSaveStatus("error");
      setErrorMessage(err.message || "Failed to save song");
    } finally {
      isSavingRef.current = false;
    }
  }, [title, artist, difficulty, key, tempo, capo, customChords, parsedBody, songId, isClone, user, rawContent]);

  // Mark dirty and trigger debounced auto-save
  const notifyFieldChanged = useCallback(() => {
    isDirtyRef.current = true;
    setSaveStatus("unsaved");

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Auto-save debounced at 1500ms
    autoSaveTimerRef.current = setTimeout(() => {
      // For existing songs, auto-save triggers on changes.
      // For new songs, saves only when change occurs and title or content is present.
      if (isDirtyRef.current) {
        performSave();
      }
    }, 1500);
  }, [performSave]);

  // Cleanup auto-save timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  // Custom Chord: Parse frets for preview
  const currentPreviewFrets = useMemo(() => {
    return customFrets.map((val) => {
      const clean = val.trim().toLowerCase();
      if (clean === "x" || clean === "-1" || clean === "") return -1;
      const num = parseInt(clean, 10);
      return isNaN(num) ? -1 : Math.max(0, num);
    });
  }, [customFrets]);

  const handleAddCustomChord = () => {
    const chordName = customChordName.trim();
    if (!chordName) return;

    setCustomChords((prev) => ({
      ...prev,
      [chordName]: {
        name: chordName,
        frets: currentPreviewFrets,
        capo: customCapo,
        shape: chordName,
      },
    }));

    setCustomChordName("");
    notifyFieldChanged();
  };

  const handleDeleteCustomChord = (chordName: string) => {
    setCustomChords((prev) => {
      const copy = { ...prev };
      delete copy[chordName];
      return copy;
    });
    notifyFieldChanged();
  };

  const handleSelectExistingCustomChord = (name: string, chord: CustomChordVariation) => {
    setCustomChordName(name);
    setCustomFrets(chord.frets.map((f) => (f < 0 ? "x" : f.toString())));
    setCustomCapo(chord.capo || 0);
  };

  if (isLoading) {
    return (
      <Box p={6} textAlign="center" bgcolor="#ffffff" minHeight="100vh">
        <CircularProgress size={36} />
        <Typography variant="body2" color="text.secondary" mt={2}>
          Loading song editor...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh", py: { xs: 1.5, sm: 2.5 } }}>
      <Container maxWidth="xl">
        {/* Top Header Bar */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1.5}
          mb={2.5}
          flexWrap="wrap"
        >
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => navigate("/MySongs")} size="small">
              <BackIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" fontWeight="800">
                {songId && !isClone ? "Edit Song" : "Create Song"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {songId && !isClone
                  ? `Editing personal song #${songId}`
                  : isClone
                  ? "Creating copy with your custom edits"
                  : "New custom song"}
              </Typography>
            </Box>
          </Box>

          {/* Right Action Bar: Status & Save Button */}
          <Box display="flex" alignItems="center" gap={1.25}>
            {saveStatus === "saving" && (
              <Chip
                icon={<CircularProgress size={14} color="inherit" />}
                label="Saving..."
                size="small"
                sx={{ bgcolor: "#eff6ff", color: "#2563eb", fontWeight: 600 }}
              />
            )}
            {saveStatus === "saved" && (
              <Chip
                icon={<SavedIcon sx={{ fontSize: 16 }} />}
                label="Saved"
                size="small"
                color="success"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            )}
            {saveStatus === "unsaved" && (
              <Chip
                label="Unsaved changes"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            )}

            {songId && !isClone && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<ViewIcon />}
                onClick={() => navigate(`/Song?id=${songId}`)}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                View
              </Button>
            )}

            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<SaveIcon />}
              onClick={performSave}
              disabled={saveStatus === "saving"}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 2,
                py: 0.6,
                fontWeight: 700,
              }}
            >
              Save
            </Button>
          </Box>
        </Box>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
            {errorMessage}
          </Alert>
        )}

        {/* View Toggle on Mobile */}
        <Box sx={{ display: { xs: "block", md: "none" }, mb: 2 }}>
          <Tabs value={activeTab} onChange={(_, val) => setActiveTab(val)} centered>
            <Tab label="Editor" />
            <Tab label="Live Preview" />
          </Tabs>
        </Box>

        <Grid container spacing={2.5}>
          {/* LEFT: Song Metadata & Content Editor */}
          <Grid
            size={{ xs: 12, md: 7 }}
            sx={{
              display: {
                xs: activeTab === 0 ? "block" : "none",
                md: "block",
              },
            }}
          >
            <Stack spacing={2.5}>
              {/* Basic Song Metadata */}
              <Card variant="outlined" sx={{ borderRadius: 2.5, bgcolor: "#ffffff" }}>
                <CardHeader
                  title={<Typography variant="subtitle1" fontWeight="bold">Song Details</Typography>}
                  sx={{ pb: 1 }}
                />
                <CardContent sx={{ pt: 1 }}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Song Title"
                        fullWidth
                        size="small"
                        required
                        value={title}
                        onChange={(e) => {
                          setTitle(e.target.value);
                          notifyFieldChanged();
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Artist"
                        fullWidth
                        size="small"
                        value={artist}
                        onChange={(e) => {
                          setArtist(e.target.value);
                          notifyFieldChanged();
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <TextField
                        select
                        label="Difficulty"
                        fullWidth
                        size="small"
                        value={difficulty}
                        onChange={(e) => {
                          setDifficulty(e.target.value);
                          notifyFieldChanged();
                        }}
                      >
                        <MenuItem value="beginner">Beginner</MenuItem>
                        <MenuItem value="intermediate">Intermediate</MenuItem>
                        <MenuItem value="advanced">Advanced</MenuItem>
                        <MenuItem value="expert">Expert</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <TextField
                        label="Key"
                        fullWidth
                        size="small"
                        placeholder="e.g. C, Am"
                        value={key}
                        onChange={(e) => {
                          setKey(e.target.value);
                          notifyFieldChanged();
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <TextField
                        label="Tempo (BPM)"
                        type="number"
                        fullWidth
                        size="small"
                        placeholder="e.g. 120"
                        value={tempo}
                        onChange={(e) => {
                          setTempo(e.target.value);
                          notifyFieldChanged();
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <TextField
                        label="Capo"
                        fullWidth
                        size="small"
                        placeholder="e.g. 2nd fret"
                        value={capo}
                        onChange={(e) => {
                          setCapo(e.target.value);
                          notifyFieldChanged();
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Custom Chords Support Section */}
              <Card variant="outlined" sx={{ borderRadius: 2.5, bgcolor: "#ffffff" }}>
                <CardHeader
                  title={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Custom Chords & Fingerings
                      </Typography>
                      <Chip
                        label={`${Object.keys(customChords).length} defined`}
                        size="small"
                        sx={{ bgcolor: "#f5f3ff", color: "#7c3aed", fontWeight: 700 }}
                      />
                    </Box>
                  }
                  subheader="Define custom chord fingerings. These will be highlighted in violet and display your exact fingerings."
                  sx={{ pb: 1 }}
                />
                <CardContent sx={{ pt: 1 }}>
                  {/* Defined Chords List */}
                  {Object.keys(customChords).length > 0 && (
                    <Box mb={2} display="flex" gap={1} flexWrap="wrap">
                      {Object.entries(customChords).map(([name, chord]) => (
                        <Chip
                          key={name}
                          label={name}
                          onClick={() => handleSelectExistingCustomChord(name, chord)}
                          onDelete={() => handleDeleteCustomChord(name)}
                          color="secondary"
                          variant="outlined"
                          sx={{
                            fontWeight: 700,
                            borderColor: "#c4b5fd",
                            color: "#7c3aed",
                            bgcolor: "#f5f3ff",
                          }}
                        />
                      ))}
                    </Box>
                  )}

                  {/* Add / Edit Custom Chord Row */}
                  <Box
                    p={2}
                    borderRadius={2}
                    sx={{ bgcolor: "#f8fafc", border: "1px dashed #cbd5e1" }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                          label="Chord Name"
                          fullWidth
                          size="small"
                          placeholder="e.g. Cadd9, D/F#"
                          value={customChordName}
                          onChange={(e) => setCustomChordName(e.target.value)}
                        />
                      </Grid>

                      {/* 6 String Fret Inputs */}
                      <Grid size={{ xs: 12, sm: 8 }}>
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                          Frets: strings 6 (Low E) to 1 (High e). Enter 0 for open, x for muted:
                        </Typography>
                        <Box display="flex" gap={1}>
                          {STRINGS_LABELS.map((label, idx) => (
                            <TextField
                              key={label}
                              label={label.split(" ")[0]}
                              size="small"
                              inputProps={{
                                style: { textAlign: "center", padding: "6px 2px", fontSize: "0.85rem" },
                              }}
                              value={customFrets[idx]}
                              onChange={(e) => {
                                const next = [...customFrets];
                                next[idx] = e.target.value;
                                setCustomFrets(next);
                              }}
                            />
                          ))}
                        </Box>
                      </Grid>

                      {/* Chord Diagram Preview & Add Button */}
                      <Grid size={{ xs: 12 }} display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box width={100} height={110} overflow="hidden">
                            <ChordDiagram
                              variation={{
                                id: "preview",
                                capo: customCapo,
                                shape: customChordName || "Preview",
                                frets: currentPreviewFrets,
                              }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Frets: {currentPreviewFrets.map((f) => (f < 0 ? "×" : f)).join(" ")}
                          </Typography>
                        </Box>

                        <Button
                          variant="outlined"
                          color="secondary"
                          size="small"
                          disabled={!customChordName.trim()}
                          onClick={handleAddCustomChord}
                          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
                        >
                          {customChords[customChordName.trim()] ? "Update Chord" : "Add Custom Chord"}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>

              {/* Chords & Lyrics Content Editor */}
              <Card variant="outlined" sx={{ borderRadius: 2.5, bgcolor: "#ffffff" }}>
                <CardHeader
                  title={<Typography variant="subtitle1" fontWeight="bold">Chords & Lyrics Content</Typography>}
                  subheader="Type chords on their own line directly above lyrics. Use [Verse], [Chorus] for sections."
                  sx={{ pb: 1 }}
                />
                <CardContent sx={{ pt: 1 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={14}
                    maxRows={30}
                    placeholder={`[Verse 1]\nAm          C\nToday is gonna be the day\nG           F\nThat they're gonna throw it back to you`}
                    value={rawContent}
                    onChange={(e) => {
                      setRawContent(e.target.value);
                      notifyFieldChanged();
                    }}
                    inputProps={{
                      style: {
                        fontFamily: "'Courier New', Courier, monospace",
                        fontSize: "0.95rem",
                        lineHeight: 1.4,
                        direction: isHebrew ? "rtl" : "ltr",
                        textAlign: isHebrew ? "right" : "left",
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        bgcolor: "#fafafa",
                      },
                    }}
                  />
                </CardContent>
              </Card>
            </Stack>
          </Grid>

          {/* RIGHT: Live Preview Pane */}
          <Grid
            size={{ xs: 12, md: 5 }}
            sx={{
              display: {
                xs: activeTab === 1 ? "block" : "none",
                md: "block",
              },
            }}
          >
            <Box sx={{ position: { md: "sticky" }, top: 76 }}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2.5,
                  bgcolor: "#ffffff",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
                  maxHeight: "88vh",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardHeader
                  title={
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography variant="subtitle1" fontWeight="bold">
                        Live Preview
                      </Typography>
                      <Chip label="Interactive" size="small" variant="outlined" sx={{ fontSize: "0.7rem" }} />
                    </Box>
                  }
                  subheader="Click any chord to preview its fingering. Custom chords appear in purple."
                  sx={{ pb: 1, borderBottom: "1px solid #f1f5f9" }}
                />

                <CardContent sx={{ flexGrow: 1, overflowY: "auto", p: { xs: 1.5, sm: 2.5 } }}>
                  <Box mb={2}>
                    <Typography variant="h5" fontWeight="bold">
                      {title || "Untitled Song"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {artist || "Unknown Artist"}
                    </Typography>
                    <Box display="flex" gap={0.5} mt={1} flexWrap="wrap">
                      {difficulty && <Chip label={difficulty} size="small" />}
                      {key && <Chip label={`Key: ${key}`} size="small" variant="outlined" />}
                      {tempo && <Chip label={`${tempo} BPM`} size="small" variant="outlined" />}
                      {capo && <Chip label={`Capo: ${capo}`} size="small" variant="outlined" />}
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  {parsedBody.length > 0 ? (
                    <SongBody
                      rows={parsedBody}
                      fontChange={0}
                      isHebrew={isHebrew}
                      customChords={customChords}
                    />
                  ) : (
                    <Typography color="text.secondary" variant="body2" sx={{ fontStyle: "italic", py: 4, textAlign: "center" }}>
                      Song content preview will appear here as you type chords and lyrics...
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
